import React from 'react';
import { Loader2 } from 'lucide-react';
import { getDietaryDisplayName } from '@/data/dietary-mapping';
import { cn } from '@/lib/utils';
import { resolveSpoonacularTagEnrichmentItem } from '@/hooks/useSpoonacularTagEnrichment';
import { TagOverflowTooltip } from '@/components/recipes/TagOverflowTooltip';

type TagTone = 'diet' | 'intolerance';

const stringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const values: string[] = [];
  for (const entry of value) {
    const normalized = String(entry ?? '').trim();
    const key = normalized.toLowerCase();
    if (!normalized || seen.has(key)) continue;
    seen.add(key);
    values.push(normalized);
  }
  return values;
};

const statusIsPending = (status: unknown): boolean => {
  const normalized = String(status ?? '').trim().toLowerCase();
  return normalized === 'not_cached' || normalized === 'queued' || normalized === 'processing';
};

const formatTagLabel = (value: string, tone: TagTone): string => {
  const normalized = value.toLowerCase().replace(/[-\s]+/g, '_');
  return getDietaryDisplayName(normalized, tone === 'diet' ? 'dietLabels' : 'healthLabels');
};

const TagGroup: React.FC<{
  label: string;
  values: string[];
  tone: TagTone;
  maxVisible: number;
}> = ({ label, values, tone, maxVisible }) => {
  if (!values.length) return null;

  const visible = values.slice(0, maxVisible);
  const allLabels = values.map((value) => formatTagLabel(value, tone));
  const visibleText = visible.map((value) => formatTagLabel(value, tone)).join(', ');
  const hiddenLabels = allLabels.slice(maxVisible);
  const textClass = tone === 'diet' ? 'text-emerald-700' : 'text-amber-700';

  return (
    <div className={cn('flex min-w-0 flex-wrap items-baseline gap-x-1 text-xs leading-snug', textClass)}>
      <span className="shrink-0 font-semibold">
        {label} -
      </span>
      <span className="min-w-0 font-medium leading-snug" title={values.map((value) => formatTagLabel(value, tone)).join(', ')}>
        {visibleText}{hiddenLabels.length ? ',' : ''}
      </span>
      <TagOverflowTooltip
        label={label.toLowerCase()}
        values={hiddenLabels.length ? allLabels : []}
        triggerLabel={`+${hiddenLabels.length} more`}
        className={cn('min-h-0 shrink-0 border-0 bg-transparent px-0 py-0 text-xs font-medium leading-snug', textClass)}
        itemClassName={cn('border-0 bg-transparent px-0 py-0', textClass)}
        showHeading={false}
      />
    </div>
  );
};

export const SpoonacularTagSummary: React.FC<{
  item: unknown;
  className?: string;
  maxVisible?: number;
}> = ({ item, className, maxVisible = 3 }) => {
  if (!item || typeof item !== 'object') return null;
  const record = item as Record<string, unknown>;
  const enrichmentItem = resolveSpoonacularTagEnrichmentItem(record);
  const isSpoonacular = Boolean(enrichmentItem);

  const dietaryTags = stringArray(record.dietaryTagIds ?? record.dietLabels ?? record.tags);
  const intoleranceTags = stringArray(record.intoleranceTagIds ?? record.intoleranceTags);
  const pending = isSpoonacular && statusIsPending(record.tagClassificationStatus);
  const failed = String(record.tagClassificationStatus ?? '').trim().toLowerCase() === 'failed';

  if (!pending && failed && dietaryTags.length === 0 && intoleranceTags.length === 0) {
    return null;
  }

  if (!pending && dietaryTags.length === 0 && intoleranceTags.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-1', className)}>
      {pending ? (
        <div className="inline-flex items-center gap-1.5 rounded-md border border-sky-100 bg-sky-50 px-2 py-1 text-xs font-medium leading-none text-sky-700">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          <span>Checking tags...</span>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-1">
        <TagGroup label="Diet" values={dietaryTags} tone="diet" maxVisible={maxVisible} />
        <TagGroup label="Intolerances" values={intoleranceTags} tone="intolerance" maxVisible={maxVisible} />
      </div>
    </div>
  );
};
